import React, { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';

import { useGlobalContext } from '../../../context/GlobalContext';
import { getNotifsAction } from '../../../actions/authActions';
import thumbUpIcon from '../../../assets/images/thumb-up.svg';
import clockIcon from '../../../assets/images/clock.svg';
import lateIcon from '../../../assets/images/late.svg';
import roleStyles from '../RoleDashboard.module.scss';
import styles from './AdminValidationDashboard.module.scss';

const DONUT_COLORS = ['#D24B4B', '#FF9D2E', '#69B8C7'];
const TILE_ICONS = {
  toTreat: thumbUpIcon,
  soonLate: clockIcon,
  late: lateIcon,
};
const TILE_CLASSES = {
  toTreat: 'toTreat',
  soonLate: 'soonLate',
  late: 'late',
};

function getEmptyDashboard() {
  return {
    toValidateByTherapeuticArea: {
      total: 0,
      items: [],
    },
    teamQuestionsByTherapeuticArea: {
      total: 0,
      items: [],
    },
    globalQuestionTiles: {
      toTreat: 0,
      soonLate: 0,
      late: 0,
    },
  };
}

function DonutCard({ title, total, items, linkTo }) {
  return (
    <section className={styles.chartCard}>
      <p className={`${roleStyles.label} ${styles.cardTitle}`}>{title}</p>
      <div className={styles.chartContent}>
        <div className={styles.legend}>
          {items.map((item, index) => (
            <div key={item.label} className={styles.legendItem}>
              <span className={styles.legendDot} style={{ backgroundColor: DONUT_COLORS[index] || '#DADADF' }} />
              <p>{item.label}</p>
            </div>
          ))}
        </div>
        <Link to={linkTo} className={styles.donutShell}>
          <div className={styles.badges}>
            {items.map((item, index) => (
              <div key={item.label} className={`${styles.badge} ${styles[`badge${index + 1}`]}`}>
                {item.value}
              </div>
            ))}
          </div>
          <div className={styles.donutChart}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={items}
                  dataKey="value"
                  nameKey="label"
                  innerRadius={48}
                  outerRadius={74}
                  stroke="none"
                  startAngle={30}
                  endAngle={390}
                  paddingAngle={4}
                >
                  {items.map((item, index) => (
                    <Cell key={item.label} fill={DONUT_COLORS[index] || '#DADADF'} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div className={styles.donutCenter}>
              <p>{total}</p>
            </div>
          </div>
        </Link>
      </div>
    </section>
  );
}

function StatusTile({ label, count, iconKey }) {
  return (
    <Link to="/question" className={roleStyles.card}>
      <div className={roleStyles.cardContent}>
        <p className={roleStyles.label}>{label}</p>
        <div className={roleStyles.countRow}>
          <p className={roleStyles.count}>{count}</p>
          <p className={roleStyles.unit}>questions</p>
        </div>
      </div>
      <div className={`${roleStyles.iconWrap} ${roleStyles[TILE_CLASSES[iconKey]]}`}>
        <img src={TILE_ICONS[iconKey]} alt="" />
      </div>
    </Link>
  );
}

export default function AdminValidationDashboard({ dashboard = getEmptyDashboard() }) {
  const [context, dispatch] = useGlobalContext();
  const firstName = context.auth.userAuth?.firstName || context.auth.userAuth?.profile?.firstName || '';

  useEffect(() => {
    getNotifsAction(dispatch, 'Medocta');
  }, [dispatch]);

  const safeDashboard = useMemo(() => ({
    ...getEmptyDashboard(),
    ...dashboard,
    toValidateByTherapeuticArea: {
      ...getEmptyDashboard().toValidateByTherapeuticArea,
      ...dashboard.toValidateByTherapeuticArea,
    },
    teamQuestionsByTherapeuticArea: {
      ...getEmptyDashboard().teamQuestionsByTherapeuticArea,
      ...dashboard.teamQuestionsByTherapeuticArea,
    },
    globalQuestionTiles: {
      ...getEmptyDashboard().globalQuestionTiles,
      ...dashboard.globalQuestionTiles,
    },
  }), [dashboard]);

  return (
    <div className={`page-container ${roleStyles.container} ${styles.page}`}>
      <h1 className={roleStyles.title}>Bonjour {firstName}</h1>

      <div className={styles.topGrid}>
        <DonutCard
          title="A valider"
          total={safeDashboard.toValidateByTherapeuticArea.total}
          items={safeDashboard.toValidateByTherapeuticArea.items}
          linkTo="/validations"
        />
        <DonutCard
          title="Questions de mon équipe"
          total={safeDashboard.teamQuestionsByTherapeuticArea.total}
          items={safeDashboard.teamQuestionsByTherapeuticArea.items}
          linkTo="/question"
        />
      </div>

      <div className={`${roleStyles.cards} ${styles.bottomTiles}`}>
        <StatusTile label="À traiter" count={safeDashboard.globalQuestionTiles.toTreat} iconKey="toTreat" />
        <StatusTile label="Bientôt en retard" count={safeDashboard.globalQuestionTiles.soonLate} iconKey="soonLate" />
        <StatusTile label="En retard" count={safeDashboard.globalQuestionTiles.late} iconKey="late" />
      </div>
    </div>
  );
}
